import { FC, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import { EditorialQueuePage } from '@/types/resources';
import { EditorialFacebookPostNetwork } from '@/utils/enums';

const Notification = styled.div`
  display: flex;
  cursor: pointer;
  gap: 8px;
  align-items: center;
  padding: 12px 16px;
  border-radius: 4px;
  border: 0.5px solid ${({ theme }) => theme.colors.error};
  background: ${({ theme }) => theme.colors.errorLight};

  strong {
    font-weight: 700;
  }

  a {
    text-decoration: underline;
  }
`;

interface Props {
  activePage: EditorialQueuePage;
}

const FailedPostsNotification: FC<Props> = ({ activePage }) => {
  const [network, networkLabel] = useNetwork();

  const theme = useTheme();

  const hasFailedPosts = useMemo(() => {
    const key = (() => {
      switch (network) {
        case EditorialFacebookPostNetwork.FACEBOOK:
          return 'hasFailedFbPosts';
        case EditorialFacebookPostNetwork.INSTAGRAM:
          return 'hasFailedIgPosts';
        case EditorialFacebookPostNetwork.THREADS:
          return 'hasFailedThreadsPosts';
        case EditorialFacebookPostNetwork.PINTEREST:
          return 'hasFailedPinterestPosts';
        case EditorialFacebookPostNetwork.TWITTER:
          return 'hasFailedTwitterPosts';
        case EditorialFacebookPostNetwork.REDDIT:
          return 'hasFailedRedditPosts';
      }
    })() as keyof EditorialQueuePage;

    return activePage[key];
  }, [activePage, network]);

  if (!hasFailedPosts) {
    return null;
  }

  return (
    <Notification>
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="8"
          cy="8"
          r="7.5"
          fill={theme.colors.error}
          stroke={theme.colors.error}
        />
        <path
          d="M7.25 5.73529V4.5H8.75V5.73529H7.25ZM7.25 11.5V6.55882H8.75V11.5H7.25Z"
          fill={theme.colors.white}
        />
      </svg>
      <Text $type="captionSemibold">
        <strong>This page has some failed posts to distribute.</strong>
        Go to the{' '}
        <Link to={`../../failed/${networkLabel}`} relative="path">
          Failed tab
        </Link>{' '}
        to see full list.
      </Text>
    </Notification>
  );
};

export default FailedPostsNotification;
